import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScrollText, Plus } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceCustomRules,
  useDeleteServiceCustomRule,
  useServiceAdvancedEnabledSites,
  type AdvancedStrategyListItem,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CustomRulesTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * AI-WAF → Custom Rules. Lists user-defined detection rules (custom_strategy
 * surface) with add / edit / delete.
 */
export function CustomRulesTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;
  const { show } = useToast();
  const [pendingDelete, setPendingDelete] = useState<AdvancedStrategyListItem | null>(null);

  const { data: rules = [], isLoading } = useServiceCustomRules(serviceId ?? '');
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId ?? '');

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceCustomRule(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'custom_rule', 'delete');
      show({ tone: 'success', title: 'Custom rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<AdvancedStrategyListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'website',
        header: 'Websites',
        cell: ({ row }) => {
          const ids = row.original.sites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${base}/aiwaf/custom-rules/${row.original.id}`)}
            >
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, siteName],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'AI-WAF' }, { label: 'Custom Rules' }]} />}
        title="Custom Rules"
        description="Create user-defined detection rules that can be applied to the protected websites."
        actions={
          <Button
            startIcon={<Plus size={16} />}
            onClick={() => navigate(`${base}/aiwaf/custom-rules/new`)}
          >
            Add Custom Rule
          </Button>
        }
      />

      <Section title="Custom Rules">
        {isLoading ? (
          <div className={styles.skeletonStack}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <DataTable
              columns={columns}
              data={rules}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<ScrollText size={20} />}
                  title="No custom rules"
                  description="Add a rule to define your own detection logic."
                />
              }
            />
          </div>
        )}
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete custom rule?"
        description={`Delete "${pendingDelete?.name}"? This removes the rule from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
